import React, { useState, useEffect, useCallback } from 'react';
import { getUserFeatureGroups, getRecentFeaturesForUser } from '../services/featureService';
import { formatRelativeDate } from '../utils/dateUtils';
import { logger, handleError } from '../utils/logger';
import { supabase } from '../lib/supabase';
import './Home.css';

const CACHE_DURATION_MS = 2 * 60 * 1000; // 2 minutes (reduced from 5 for better freshness)

const Home = ({ onSelectFeatureGroup, onOpenFeature }) => {
  const [featureMaps, setFeatureMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentFeatures, setRecentFeatures] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [recentError, setRecentError] = useState(null);
  const [userMap, setUserMap] = useState(new Map()); // Map of user_id to user info
  
  // Cache keys for localStorage
  const FEATURE_MAPS_CACHE_KEY = 'home_feature_maps_cache';
  const RECENT_FEATURES_CACHE_KEY = 'home_recent_features_cache';
  const CACHE_TIMESTAMP_KEY = 'home_cache_timestamp';

  const loadCachedData = useCallback(() => {
    try {
      const cachedMaps = localStorage.getItem(FEATURE_MAPS_CACHE_KEY);
      const cachedFeatures = localStorage.getItem(RECENT_FEATURES_CACHE_KEY);
      const cacheTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
      
      const now = Date.now();
      const isCacheValid = cacheTimestamp && (now - parseInt(cacheTimestamp)) < CACHE_DURATION_MS;
      
      if (isCacheValid && cachedMaps) {
        try {
          const maps = JSON.parse(cachedMaps);
          setFeatureMaps(maps);
          setLoading(false);
        } catch (e) {
          logger.error('Error parsing cached feature maps:', e);
        }
      }
      
      if (isCacheValid && cachedFeatures) {
        try {
          const features = JSON.parse(cachedFeatures);
          setRecentFeatures(features);
          setRecentLoading(false);
        } catch (e) {
          logger.error('Error parsing cached recent features:', e);
        }
      }
    } catch (err) {
      logger.error('Error loading cached data:', err);
    }
  }, []);

  const saveToCache = (maps, features) => {
    try {
      if (maps) {
        localStorage.setItem(FEATURE_MAPS_CACHE_KEY, JSON.stringify(maps));
      }
      if (features) {
        localStorage.setItem(RECENT_FEATURES_CACHE_KEY, JSON.stringify(features));
      }
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (err) {
      logger.error('Error saving to cache:', err);
    }
  };

  const featureMapsEqual = (a, b) => {
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    
    const aIds = new Set(a.map(m => m.id));
    const bIds = new Set(b.map(m => m.id));
    if (aIds.size !== bIds.size) return false;
    
    for (const id of aIds) {
      if (!bIds.has(id)) return false;
      const aItem = a.find(m => m.id === id);
      const bItem = b.find(m => m.id === id);
      // Compare key fields that might change
      if (aItem.feature_count !== bItem.feature_count ||
          aItem.user_count !== bItem.user_count ||
          aItem.dimensions_count !== bItem.dimensions_count ||
          aItem.display_name !== bItem.display_name ||
          aItem.role !== bItem.role) {
        return false;
      }
    }
    return true;
  };

  const recentFeaturesEqual = (a, b) => {
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    
    // Compare by ID and updated_at - if same IDs with same updated_at, consider equal
    const aMap = new Map(a.map(f => [f.id, f.updated_at]));
    const bMap = new Map(b.map(f => [f.id, f.updated_at]));
    if (aMap.size !== bMap.size) return false;
    
    for (const [id, updatedAt] of aMap) {
      if (bMap.get(id) !== updatedAt) return false;
    }
    return true;
  };

  // Load user info for updated_by fields
  const loadUserInfo = useCallback(async (features) => {
    const userIds = features
      .map(f => f.updated_by)
      .filter(Boolean)
      .filter((id, index, self) => self.indexOf(id) === index); // Get unique IDs

    if (userIds.length === 0) {
      return;
    }

    try {
      const { data: userInfo, error } = await supabase.rpc('get_users_by_ids', {
        p_user_ids: userIds
      });

      if (error) {
        logger.warn('Error fetching user info:', error);
        return;
      }

      const newUserMap = new Map();
      (userInfo || []).forEach(user => {
        newUserMap.set(user.user_id, {
          email: user.email || 'Unknown',
          full_name: user.full_name || 'Unknown'
        });
      });

      setUserMap(newUserMap);
    } catch (err) {
      logger.warn('Error loading user info:', err);
    }
  }, []);

  const getRoleBadgeClass = useCallback((role) => {
    return `role-badge role-${role.toLowerCase()}`;
  }, []);

  const getMapNameForFeature = useCallback((featureGroupId) => {
    const map = featureMaps.find((m) => m.id === featureGroupId);
    return map ? map.display_name : 'Unknown map';
  }, [featureMaps]);

  const formatUpdatedAt = useCallback((updatedAt) => {
    if (!updatedAt) return 'Unknown';
    return formatRelativeDate(updatedAt);
  }, []);

  const refreshData = useCallback(async () => {
    // Load feature maps and recent features in parallel
    const [mapsResult, featuresResult] = await Promise.allSettled([
      getUserFeatureGroups(),
      getRecentFeaturesForUser(20)
    ]);

    // Handle feature maps
    if (mapsResult.status === 'fulfilled') {
      const maps = mapsResult.value;
      setFeatureMaps(prevMaps => {
        if (!featureMapsEqual(prevMaps, maps)) {
          saveToCache(maps, null);
          return maps;
        }
        return prevMaps;
      });
      setError(null);
    } else {
      logger.error('Error loading feature maps:', mapsResult.reason);
      setFeatureMaps(prevMaps => {
        if (prevMaps.length === 0) {
          setError(handleError(mapsResult.reason, 'Failed to load feature maps. Please try again.'));
        }
        return prevMaps;
      });
    }
    
    if (loading) {
      setLoading(false);
    }

    // Handle recent features
    if (featuresResult.status === 'fulfilled') {
      const features = featuresResult.value;
      setRecentFeatures(prevFeatures => {
        if (!recentFeaturesEqual(prevFeatures, features)) {
          saveToCache(null, features);
          // Fetch user info for updated_by fields
          if (features && features.length > 0) {
            loadUserInfo(features);
          }
          return features;
        }
        return prevFeatures;
      });
      setRecentError(null);
    } else {
      logger.error('Error loading recent features:', featuresResult.reason);
      setRecentFeatures(prevFeatures => {
        if (prevFeatures.length === 0) {
          setRecentError(handleError(featuresResult.reason, 'Failed to load recent features.'));
        }
        return prevFeatures;
      });
    }
    
    if (recentLoading) {
      setRecentLoading(false);
    }
  }, [loading, recentLoading, loadUserInfo]);

  // Load cached data on mount (after refreshData is defined)
  useEffect(() => {
    loadCachedData();
    // Then refresh in background
    refreshData();

    // Set up periodic refresh
    const intervalId = setInterval(() => {
      refreshData();
    }, CACHE_DURATION_MS);

    return () => clearInterval(intervalId);
  }, [refreshData, loadCachedData]);

  if (loading) {
    return (
      <div className="home-container">
        <div className="loading">Loading feature maps...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="home-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="home-container">
      <div className="home-layout">
        <div className="home-main">
          <div className="home-header">
            <h2>Feature Maps</h2>
            <p className="home-subtitle">Select a feature map to get started</p>
          </div>

          {featureMaps.length === 0 ? (
            <div className="no-feature-maps">
              <p>You don't have access to any feature maps yet.</p>
              <p>Contact an administrator to be added to a feature map.</p>
            </div>
          ) : (
            <div className="feature-maps-grid">
              {featureMaps.map((map) => (
                <div
                  key={map.id}
                  className="feature-map-card"
                  onClick={() => onSelectFeatureGroup && onSelectFeatureGroup(map.id)}
                >
                  <div className="card-header">
                    <h3 className="card-title">{map.display_name}</h3>
                    <span className={getRoleBadgeClass(map.role)}>
                      {map.role}
                    </span>
                  </div>
                  <div className="card-stats">
                    <div className="stat-item">
                      <span className="stat-label">Features</span>
                      <span className="stat-value">{map.feature_count || 0}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Users</span>
                      <span className="stat-value">{map.user_count || 0}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Dimensions</span>
                      <span className="stat-value">{map.dimensions_count || 0}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <aside className="home-sidebar">
          <div className="recent-features-panel">
            <h3 className="recent-features-title">Recently Updated Features</h3>
            {recentLoading && (
              <div className="recent-features-loading">Loading recent features...</div>
            )}
            {!recentLoading && recentError && (
              <div className="recent-features-error">{recentError}</div>
            )}
            {!recentLoading && !recentError && recentFeatures.length === 0 && (
              <div className="recent-features-empty">
                No recently updated features found.
              </div>
            )}
            {!recentLoading && !recentError && recentFeatures.length > 0 && (
              <ul className="recent-features-list">
                {recentFeatures.map((feature) => (
                  <li key={feature.id} className="recent-feature-item">
                    <button
                      type="button"
                      className="recent-feature-button"
                      onClick={() => onOpenFeature && onOpenFeature(feature.id)}
                    >
                      <div className="recent-feature-name">{feature.name}</div>
                      <div className="recent-feature-meta">
                        <span className="recent-feature-map">
                          {getMapNameForFeature(feature.feature_group_id)}
                        </span>
                        {feature.updated_by && userMap.has(feature.updated_by) ? (
                          <span className="recent-feature-user">
                            {userMap.get(feature.updated_by).full_name}
                          </span>
                        ) : feature.updated_by ? (
                          <span className="recent-feature-user">Loading...</span>
                        ) : null}
                        <span className="recent-feature-updated">
                          {formatUpdatedAt(feature.updated_at)}
                        </span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Home;

